import {
  MultiSourceLightingAnalyzer,
  OpticalFlowAnalyzer,
  ExposureAnalyzer,
  EdgeConfidenceAnalyzer,
  ScreenQualityAnalyzer,
  SpillDetectionAnalyzer,
  TrackingMarkerAnalyzer,
  VideoArtifactsAnalyzer,
} from '@limonade/eye-analyzers';
import {
  LimonadeEyeEngine,
  type LimonadeEyeFrameResult,
  type LimonadeEyePerformanceMode,
  type LightingSetupMetrics,
  type OpticalFlowMetrics,
  type OpticalFlowResult,
  MotionLinkProcessor,
  type MotionLinkProcessorResult,
  type ExposureMetrics,
  type EdgeConfidenceMetrics,
  type ScreenQualityMetrics,
  type SpillDetectionMetrics,
  type TrackingMarkerMetrics,
  type VideoArtifactsResult,
} from '@limonade/eye-core';
import {
  createMotionLinkAIVideoAudit,
  createLimonadeEyeReport,
  exportMotionLinkFBX,
  exportMotionLinkUSD,
  LimonadeEyeJSONExporter,
} from '@limonade/eye-exporters';
import './styles.css';
import { captureVideoFrame, renderLuminanceHistogram, renderOverlayLayers } from './framePipeline.js';
import { installChromaSelfTest } from './demoSelfTest.js';
import { mountPCEPlayground } from './pcePlayground.js';
import sampleMotionLink from '../../../docs/sample-motionlink.json';
import {
  cleanMotionLinkVisualizationData,
  type MotionLinkCleaningOptions,
  type MotionLinkCleaningStats,
} from './motionlinkDataCleaner.js';
import { MotionLinkJsonLoader, type MotionLinkJsonLoadResult } from './motionlinkJsonLoader.js';
import { MotionLinkVisualizerCanvas } from './motionlinkVisualizerCanvas.js';
import {
  createMotionLinkRawVsCleanedAnalysis,
  normalizeMotionLinkVisualizerState,
  type MotionLinkAnimateSource,
  type MotionLinkComparisonMetrics,
  type MotionLinkOrientationGraphData,
  type MotionLinkRawVsCleanedAnalysis,
} from './motionlinkRawVsCleaned.js';

const app = document.querySelector<HTMLDivElement>('#app');
if (!app) throw new Error('Demo root element was not found.');

app.innerHTML = `
  <main>
    <header>
      <div class="eyebrow">LOCAL-FIRST VIDEO INTELLIGENCE</div>
      <h1>Limonade <span>EYE™</span></h1>
      <p>Browser frame pipeline · local vision analysis</p>
      <nav class="app-tabs" aria-label="Limonade EYE tools">
        <button id="chroma-tab" class="app-tab active" type="button" role="tab" aria-selected="true" aria-controls="chroma-view">
          Vision Analysis
        </button>
        <button id="pce-tab" class="app-tab" type="button" role="tab" aria-selected="false" aria-controls="pce-view">
          PCE Color Match
        </button>
        <button id="motionlink-tab" class="app-tab" type="button" role="tab" aria-selected="false" aria-controls="motionlink-view">
          MotionLink™
        </button>
      </nav>
    </header>
    <section id="pce-view" class="app-view" role="tabpanel" aria-labelledby="pce-tab" hidden>
      <section id="pce-playground" class="panel pce-playground" aria-label="PCE reference and source image comparison"></section>
    </section>
    <section id="motionlink-view" class="app-view" role="tabpanel" aria-labelledby="motionlink-tab" hidden>
      <section class="motionlink-console" aria-label="MotionLink camera motion workspace">
        <section class="panel motionlink-hero">
          <div>
            <div class="eyebrow">MOTIONLINK™ · CAMERA MOTION PIPELINE</div>
            <h2>IMU + ARKit + Optical Flow → Camera Path</h2>
            <p>
              MotionLink is the bridge between a phone mounted on a camera head and usable camera motion data
              for VFX, AI video, Unreal, and future ChromaPulp workflows.
            </p>
          </div>
          <div class="motionlink-live-state">
            <strong>SDK foundation ready</strong>
            <span>Developer iPhone capture, AI video audit, and FBX/USD camera-path exports are available as v0.1 test tools.</span>
          </div>
        </section>

        <section class="panel motionlink-processor-panel" aria-label="MotionLink JSON processor">
          <div class="panel-title-row">
            <div>
              <h3>MotionLink Capture Test Console</h3>
              <p>Load an exported iPhone MotionLink JSON file and process it locally through the SDK.</p>
            </div>
            <span class="motionlink-pill">LOCAL PROCESSOR</span>
          </div>
          <div class="motionlink-visualizer-layout">
            <section class="motionlink-input-panel" aria-label="MotionLink JSON input">
              <label class="motionlink-file-picker">
                <input id="motionlink-file" type="file" accept="application/json,.json" />
                <strong>Choose MotionLink JSON</strong>
                <small id="motionlink-file-name">No file selected</small>
              </label>
              <textarea id="motionlink-json-paste" spellcheck="false" placeholder="Paste MotionLink JSON here..."></textarea>
              <div class="motionlink-input-actions">
                <button id="motionlink-load-paste" class="secondary-button" type="button">Load pasted JSON</button>
                <button id="motionlink-load-sample" class="secondary-button" type="button">Load sample JSON</button>
              </div>
              <label class="motionlink-option">
                <input id="motionlink-use-optical-flow" type="checkbox" checked />
                Compare with Vision tab Optical Flow when available
              </label>
              <section class="motionlink-cleanup-panel" aria-label="MotionLink data cleanup controls">
                <div>
                  <strong>Data cleanup preview</strong>
                  <small>Non-destructive smoothing for noisy capture data.</small>
                </div>
                <label class="motionlink-option">
                  <input id="motionlink-clean-enabled" type="checkbox" checked />
                  Clean noisy preview
                </label>
                <label class="motionlink-clean-strength">
                  <span>Strength</span>
                  <select id="motionlink-clean-strength">
                    <option value="light">Light</option>
                    <option value="balanced" selected>Balanced</option>
                    <option value="strong">Strong</option>
                  </select>
                </label>
                <div class="motionlink-clean-stats">
                  <span>Raw <b id="motionlink-raw-distance">—</b></span>
                  <span>Clean <b id="motionlink-clean-distance">—</b></span>
                  <span>Adjusted <b id="motionlink-clean-adjusted">—</b></span>
                </div>
              </section>
            </section>

            <section class="motionlink-canvas-panel" aria-label="MotionLink 2D visualizer">
              <canvas id="motionlink-visualizer-canvas" aria-label="MotionLink motion path visualizer"></canvas>
              <div class="motionlink-playback-controls">
                <button id="motionlink-play-toggle" type="button" disabled>Play</button>
                <button id="motionlink-restart" class="secondary-button" type="button" disabled>Restart</button>
                <label>
                  <span>Speed</span>
                  <select id="motionlink-playback-speed">
                    <option value="0.25">0.25×</option>
                    <option value="0.5">0.5×</option>
                    <option value="1" selected>1×</option>
                    <option value="2">2×</option>
                  </select>
                </label>
                <button id="motionlink-export-snapshot" class="secondary-button" type="button" disabled>Export snapshot PNG</button>
              </div>
              <div class="motionlink-timeline-row">
                <input id="motionlink-timeline" type="range" min="0" max="0" value="0" step="1" disabled />
                <span id="motionlink-time-readout">0 ms / 0 ms</span>
              </div>
              <div class="motionlink-visual-toggles">
                <label class="motionlink-select-toggle">
                  <span>Path</span>
                  <select id="motionlink-path-mode">
                    <option value="cleaned" selected>Cleaned</option>
                    <option value="raw">Raw</option>
                    <option value="both">Both</option>
                  </select>
                </label>
                <button id="motionlink-animate-source" class="secondary-button mini" type="button" disabled>
                  Animate raw path
                </button>
                <label><input id="motionlink-show-grid" type="checkbox" checked /> Grid</label>
                <label><input id="motionlink-show-path" type="checkbox" checked /> Path</label>
                <label><input id="motionlink-show-keyframes" type="checkbox" checked /> Keyframes</label>
                <label><input id="motionlink-show-confidence" type="checkbox" checked /> Confidence</label>
              </div>
            </section>

            <aside class="motionlink-quality-panel" aria-label="Motion capture quality">
              <h3>Motion Capture Quality</h3>
              <p>Visual consistency preview — not a calibrated camera solve.</p>
              <div class="motionlink-quality-grid">
                <div><small>Schema</small><strong id="motionlink-schema-status">—</strong></div>
                <div><small>Continuity</small><strong id="motionlink-continuity">—</strong></div>
                <div><small>Dropped / gaps</small><strong id="motionlink-dropped-samples">—</strong></div>
                <div><small>Timestamp gaps</small><strong id="motionlink-timestamp-gaps">—</strong></div>
                <div><small>Distance</small><strong id="motionlink-distance">—</strong></div>
                <div><small>Avg confidence</small><strong id="motionlink-average-confidence">—</strong></div>
                <div><small>Max jitter</small><strong id="motionlink-max-jitter">—</strong></div>
                <div><small>Position data</small><strong id="motionlink-position-state">—</strong></div>
              </div>
            </aside>
          </div>
          <section class="motionlink-raw-cleaned-panel" aria-label="Raw versus cleaned MotionLink analysis">
            <div class="panel-title-row">
              <div>
                <h3>Raw vs Cleaned Motion Analysis</h3>
                <p id="motionlink-raw-cleaned-copy">
                  Raw vs Cleaned shows how MotionLink stabilizes sensor capture for visual inspection. This is not a calibrated 3D camera solve.
                </p>
              </div>
              <span class="motionlink-pill">HEURISTIC PREVIEW</span>
            </div>
            <div class="motionlink-comparison-grid">
              <article>
                <h4>Raw motion data</h4>
                <div class="motionlink-metric-list" id="motionlink-raw-metrics"></div>
              </article>
              <article class="accent">
                <h4>Cleaned / processed motion data</h4>
                <div class="motionlink-metric-list" id="motionlink-cleaned-metrics"></div>
              </article>
            </div>
            <div class="motionlink-analysis-footer">
              <section>
                <h4>Movement labels from cleaned path</h4>
                <ul id="motionlink-movement-labels" class="motionlink-data-list compact">
                  <li>Load MotionLink JSON to detect movement labels.</li>
                </ul>
              </section>
              <section>
                <h4>Orientation graph · yaw / pitch / roll</h4>
                <canvas id="motionlink-orientation-graph" aria-label="Yaw, pitch, and roll graph"></canvas>
              </section>
            </div>
          </section>
          <div class="motionlink-summary-grid">
            <div><small>Session</small><strong id="motionlink-session-id">—</strong></div>
            <div><small>Duration</small><strong id="motionlink-duration">—</strong></div>
            <div><small>Samples</small><strong id="motionlink-sample-count">—</strong></div>
            <div><small>Estimates</small><strong id="motionlink-estimate-count">—</strong></div>
            <div><small>Motion type</small><strong id="motionlink-dominant-motion">—</strong></div>
            <div><small>Confidence</small><strong id="motionlink-confidence">—</strong></div>
          </div>
          <div class="motionlink-prompt live" id="motionlink-processed-prompt">
            Upload a MotionLink JSON export to generate a camera-motion prompt.
          </div>
          <div class="motionlink-processor-actions">
            <button id="motionlink-download-processed" class="secondary-button" type="button" disabled>
              Download processed JSON
            </button>
            <button id="motionlink-download-usd" class="secondary-button" type="button" disabled>
              Download USD camera
            </button>
            <button id="motionlink-download-fbx" class="secondary-button" type="button" disabled>
              Download FBX camera
            </button>
            <span id="motionlink-processor-status">Waiting for MotionLink export.</span>
          </div>
          <section class="motionlink-audit-panel" aria-label="AI video motion audit">
            <div class="panel-title-row">
              <div>
                <h3>AI Video Motion Audit</h3>
                <p>Compare MotionLink intended motion against Optical Flow from the generated video.</p>
              </div>
              <span class="motionlink-pill">PIXEL AUDIT</span>
            </div>
            <p class="motionlink-audit-copy">
              Load/analyze the generated video in Vision Analysis, then load the MotionLink JSON here with Optical Flow comparison enabled.
              This audits prompt adherence; it is not a calibrated matchmove solve.
            </p>
            <div class="motionlink-quality-grid">
              <div><small>Adherence</small><strong id="motionlink-audit-score">—</strong></div>
              <div><small>Direction</small><strong id="motionlink-audit-direction">—</strong></div>
              <div><small>Actual visual motion</small><strong id="motionlink-audit-actual">—</strong></div>
              <div><small>Disagreement</small><strong id="motionlink-audit-disagreement">—</strong></div>
            </div>
            <ul id="motionlink-audit-list" class="motionlink-data-list compact">
              <li>Waiting for MotionLink + Optical Flow comparison.</li>
            </ul>
          </section>
          <section class="motionlink-grid two compact">
            <article>
              <h3>Motion Segments</h3>
              <ul id="motionlink-segment-list" class="motionlink-data-list">
                <li>Upload a MotionLink JSON file to inspect segments.</li>
              </ul>
            </article>
            <article>
              <h3>Warnings</h3>
              <ul id="motionlink-warning-list" class="motionlink-data-list">
                <li class="clear">No file processed yet.</li>
              </ul>
            </article>
          </section>
          <details class="motionlink-raw-details">
            <summary>Processed MotionLink JSON</summary>
            <pre id="motionlink-raw-output">{ "status": "waiting_for_motionlink_json" }</pre>
          </details>
        </section>

        <section class="motionlink-flow panel" aria-label="MotionLink pipeline flow">
          <article>
            <small>01</small>
            <strong>iPhone Sensors</strong>
            <span>CoreMotion IMU: gyro, accelerometer, orientation.</span>
          </article>
          <div class="motionlink-arrow">+</div>
          <article>
            <small>02</small>
            <strong>ARKit Pose</strong>
            <span>Camera transform, tracking confidence, local position.</span>
          </article>
          <div class="motionlink-arrow">+</div>
          <article>
            <small>03</small>
            <strong>Optical Flow</strong>
            <span>2D visual motion from Limonade EYE video frames.</span>
          </article>
          <div class="motionlink-arrow">→</div>
          <article class="accent">
            <small>04</small>
            <strong>Accurate Camera Path</strong>
            <span>Calibrated, smoothed, confidence-scored motion estimates.</span>
          </article>
        </section>

        <section class="motionlink-grid">
          <article class="panel motionlink-card">
            <div class="motionlink-card-title">
              <span class="status-dot ready"></span>
              <h3>MotionLink Schema</h3>
            </div>
            <p>Stores IMU samples, ARKit pose samples, frame timestamps, rig calibration, and confidence summaries.</p>
            <ul>
              <li>Schema: <code>limonade-motionlink/v1</code></li>
              <li>Motion types: pan, tilt, roll, push, pull, orbit, crane, shake</li>
              <li>Local JSON-first workflow</li>
            </ul>
          </article>

          <article class="panel motionlink-card">
            <div class="motionlink-card-title">
              <span class="status-dot ready"></span>
              <h3>Rig Calibration</h3>
            </div>
            <p>Maps phone motion into approximate cinema-camera motion space.</p>
            <ul>
              <li>Neutral forward baseline</li>
              <li>Top, side-left, side-right, upside-down, custom mounts</li>
              <li>Rotation, position, lens offset, scale, stabilization</li>
            </ul>
          </article>

          <article class="panel motionlink-card">
            <div class="motionlink-card-title">
              <span class="status-dot ready"></span>
              <h3>Sensor Fusion Processor</h3>
            </div>
            <p>Converts MotionLink samples into frame-aligned camera-motion estimates.</p>
            <ul>
              <li>Interpolates motion to video timestamps</li>
              <li>Smooths noisy readings</li>
              <li>Classifies pan, tilt, roll, push/pull, shake</li>
            </ul>
          </article>

          <article class="panel motionlink-card">
            <div class="motionlink-card-title">
              <span class="status-dot partial"></span>
              <h3>Visual Cross-Check</h3>
            </div>
            <p>Optical Flow already estimates rough 2D video motion. Future fusion compares it against IMU/ARKit motion.</p>
            <ul>
              <li>Detect sensor/visual disagreement</li>
              <li>Improve confidence scoring</li>
              <li>Flag slip, drift, or bad sync</li>
            </ul>
          </article>
        </section>

        <section class="motionlink-grid two">
          <article class="panel motionlink-export-panel">
            <div class="panel-title-row">
              <h3>Export Targets</h3>
              <span class="motionlink-pill">SDK EXPORTERS</span>
            </div>
            <div class="motionlink-export-list">
              <div><strong>MotionLink JSON</strong><span>Full local session and processed camera estimates.</span><em>Ready</em></div>
              <div><strong>CSV</strong><span>Spreadsheet-friendly per-frame motion rows.</span><em>Ready</em></div>
              <div><strong>Camera Path JSON</strong><span>Simplified path for downstream integrations.</span><em>Ready</em></div>
              <div><strong>AI Prompt Helper</strong><span>Human-readable camera move descriptions.</span><em>Ready</em></div>
              <div><strong>FBX / USD</strong><span>Approximate camera-path exchange for testing.</span><em>v0.1</em></div>
              <div><strong>AI Video Audit</strong><span>Checks generated video motion against MotionLink intent.</span><em>v0.1</em></div>
            </div>
          </article>

          <article class="panel motionlink-prompt-panel">
            <div class="panel-title-row">
              <h3>AI Video Prompt Helper</h3>
              <span class="motionlink-pill">EXAMPLE</span>
            </div>
            <div class="motionlink-prompt">
              slow handheld push-in with slight pan right
            </div>
            <pre>{
  "motion_prompt": "slow handheld push-in with slight pan right",
  "camera_movement": {
    "type": "push_in",
    "pan_degrees": 4.2,
    "tilt_degrees": -1.1,
    "shake_intensity": 0.18
  }
}</pre>
          </article>
        </section>

        <section class="panel motionlink-integration" aria-label="MotionLink integrations">
          <div>
            <div class="eyebrow">INTEGRATION ROADMAP</div>
            <h3>From phone-mounted camera data to production motion</h3>
            <p>
              The intended path is: IMU + ARKit + optical flow → accurate camera path →
              FBX/USD export → ChromaPulp, Unreal, AI video, and virtual production integrations.
            </p>
          </div>
          <div class="motionlink-integration-grid">
            <article><strong>ChromaPulp</strong><span>Future bridge for keyed footage and camera motion.</span></article>
            <article><strong>Unreal</strong><span>Future camera path import via FBX/USD conventions.</span></article>
            <article><strong>AI Video</strong><span>Camera-motion prompts and structured movement metadata.</span></article>
            <article><strong>VFX / QC</strong><span>Motion confidence, drift, sync, and shake warnings.</span></article>
          </div>
        </section>

        <section class="motionlink-grid two">
          <article class="panel motionlink-checklist">
            <h3>Current SDK Status</h3>
            <ul>
              <li><span class="status-dot ready"></span> MotionLink JSON schema</li>
              <li><span class="status-dot ready"></span> Camera rig calibration model</li>
              <li><span class="status-dot ready"></span> Sensor fusion processor</li>
              <li><span class="status-dot ready"></span> JSON / CSV / camera path exporters</li>
              <li><span class="status-dot ready"></span> AI prompt helper</li>
              <li><span class="status-dot ready"></span> iPhone capture test app</li>
              <li><span class="status-dot ready"></span> Browser JSON processing console</li>
              <li><span class="status-dot ready"></span> FBX / USD camera export v0.1</li>
              <li><span class="status-dot future"></span> ChromaPulp / Unreal bridge</li>
            </ul>
          </article>

          <article class="panel motionlink-boundary">
            <strong>Important boundary</strong>
            <span>
              This tab processes exported MotionLink files locally. It does not connect live to an iPhone,
              solve a full 3D camera, validate production FBX/USD exchange, or integrate with ChromaPulp yet.
            </span>
          </article>
        </section>
      </section>
    </section>
    <section id="chroma-view" class="app-view" role="tabpanel" aria-labelledby="chroma-tab">
      <section class="workspace">
      <div class="viewer panel">
        <label class="file-picker">
          <input id="video-file" type="file" accept="video/*" />
          <strong>Choose a local video</strong>
          <small>The file never leaves this browser.</small>
        </label>
        <div class="stage">
          <video id="video" controls playsinline></video>
          <canvas id="overlay-canvas" aria-label="Unified analyzer overlay"></canvas>
          <div id="empty-state">Load a video to begin</div>
        </div>
        <section class="overlay-controls" aria-label="Analyzer overlay controls">
          <div class="overlay-controls-heading">
            <strong>Video Overlays</strong>
            <span id="overlay-render-status">Waiting for analysis</span>
          </div>
          <div class="overlay-module-toggles">
            <label><input id="screen-overlay-toggle" type="checkbox" checked /><span class="overlay-dot screen"></span>Screen Quality</label>
            <label><input id="spill-overlay-toggle" type="checkbox" checked /><span class="overlay-dot spill"></span>Spill Detection</label>
            <label><input id="lighting-overlay-toggle" type="checkbox" checked /><span class="overlay-dot lighting"></span>Lighting Setup</label>
            <label><input id="exposure-overlay-toggle" type="checkbox" checked /><span class="overlay-dot exposure"></span>Exposure</label>
            <label><input id="edge-overlay-toggle" type="checkbox" checked /><span class="overlay-dot edge"></span>Edge Confidence</label>
          </div>
          <div id="overlay-legend" class="overlay-legend" aria-label="Active overlay legend">
            <span>No overlay primitives yet.</span>
          </div>
        </section>
        <canvas id="frame-canvas" aria-label="Captured video frame"></canvas>
        <div class="controls">
          <button id="analyze" type="button" disabled>Analyze current frame</button>
          <span id="status">Waiting for video</span>
        </div>
        <section class="pipeline-debug" aria-label="Pipeline debugging information">
          <div class="debug-heading"><strong>Pipeline Debug</strong><span>LOCAL</span></div>
          <label class="performance-mode-control">
            <span>Performance mode</span>
            <select id="performance-mode">
              <option value="quality">Quality · every analyzer every frame</option>
              <option value="balanced" selected>Balanced · reuse heavy analyzers</option>
              <option value="realtime">Realtime · lighter cadence</option>
            </select>
          </label>
          <div class="debug-grid">
            <div><small>Frame</small><strong id="frame-number">—</strong></div>
            <div><small>Timestamp</small><strong id="frame-timestamp">—</strong></div>
            <div><small>Total analysis</small><strong id="analysis-time">—</strong></div>
          </div>
          <h3>Analyzer timings</h3>
          <ul id="analyzer-timings"><li>Waiting for analysis.</li></ul>
          <h3>Analyzer status</h3>
          <ul id="analyzer-status"></ul>
        </section>
      </div>
      <aside class="panel results">
        <div class="result-heading">
          <h2>Analysis</h2>
          <span>LOCAL CV</span>
        </div>
        <section class="quality-summary" aria-label="Screen quality summary">
          <div class="quality-grid">
            <div><small>Screen</small><strong id="screen-type">—</strong></div>
            <div><small>Quality</small><strong id="quality-score">—</strong></div>
            <div><small>Coverage</small><strong id="coverage">—</strong></div>
            <div><small>Uniformity</small><strong id="uniformity">—</strong></div>
          </div>
          <h3>Warnings</h3>
          <ul id="quality-warnings"><li>Load a frame to analyze screen quality.</li></ul>
        </section>
        <section class="spill-summary" aria-label="Spill detection summary">
          <div class="panel-title-row">
            <h3>Spill Detection</h3>
          </div>
          <div class="quality-grid">
            <div><small>Risk</small><strong id="spill-risk">—</strong></div>
            <div><small>Score</small><strong id="spill-score">—</strong></div>
            <div class="wide"><small>Affected area</small><strong id="spill-area">—</strong></div>
          </div>
          <h3>Warnings</h3>
          <ul id="spill-warnings"><li>Load a frame to analyze spill.</li></ul>
        </section>
        <section class="lighting-summary" aria-label="Lighting setup summary">
          <div class="panel-title-row"><h3>Lighting Setup</h3></div>
          <div class="quality-grid">
            <div class="wide"><small>Lighting pattern</small><strong id="lighting-pattern">—</strong></div>
            <div class="wide"><small>Dominant apparent source</small><strong id="dominant-light">—</strong></div>
            <div><small>Balance</small><strong id="lighting-balance">—</strong></div>
            <div><small>Lighting ratio</small><strong id="lighting-ratio">—</strong></div>
          </div>
          <h3>Detected Lighting Sources</h3>
          <ul id="detected-lights" class="light-source-list"><li>Load a frame to estimate apparent sources.</li></ul>
          <h3>Warnings</h3>
          <ul id="lighting-warnings"><li>Load a frame to analyze lighting.</li></ul>
        </section>
        <section class="screen-lighting-summary" aria-label="Screen lighting uniformity">
          <div class="panel-title-row"><h3>Screen-Lighting Uniformity</h3></div>
          <div class="quality-grid">
            <div><small>Screen</small><strong id="screen-light-type">—</strong></div>
            <div><small>Uniformity</small><strong id="screen-light-uniformity">—</strong></div>
            <div class="wide"><small>Apparent screen lights</small><strong id="screen-light-count">—</strong></div>
          </div>
        </section>
        <section class="exposure-summary" aria-label="Exposure summary">
          <div class="panel-title-row"><h3>Exposure</h3></div>
          <div class="quality-grid">
            <div class="wide"><small>Status</small><strong id="exposure-status">—</strong></div>
            <div><small>Average luma</small><strong id="average-luma">—</strong></div>
            <div><small>Contrast</small><strong id="contrast-score">—</strong></div>
            <div><small>Highlight clip</small><strong id="highlight-clip">—</strong></div>
            <div><small>Shadow clip</small><strong id="shadow-clip">—</strong></div>
          </div>
          <canvas id="luma-histogram" aria-label="Luminance histogram"></canvas>
          <h3>Warnings</h3>
          <ul id="exposure-warnings"><li>Load a frame to analyze exposure.</li></ul>
        </section>
        <section class="edge-summary" aria-label="Edge confidence summary">
          <div class="panel-title-row"><h3>Edge Confidence</h3></div>
          <div class="quality-grid">
            <div class="wide"><small>Score</small><strong id="edge-score">—</strong></div>
            <div><small>Weak edges</small><strong id="weak-edges">—</strong></div>
            <div><small>Chroma separation</small><strong id="chroma-separation">—</strong></div>
          </div>
          <h3>Warnings</h3>
          <ul id="edge-warnings"><li>Load a frame to analyze edge confidence.</li></ul>
        </section>
        <section class="artifacts-summary" aria-label="Video artifacts summary">
          <div class="panel-title-row">
            <h3>Video Artifacts</h3>
            <label class="overlay-toggle">
              <input id="artifact-overlay-toggle" type="checkbox" checked />
              Overlay
            </label>
          </div>
          <div class="quality-grid">
            <div><small>Artifact score</small><strong id="artifact-score">—</strong></div>
            <div><small>Overall risk</small><strong id="artifact-risk">—</strong></div>
          </div>
          <h3>Detected artifacts</h3>
          <ul id="detected-artifacts"><li>Load a frame to run video QC.</li></ul>
          <h3>Warnings</h3>
          <ul id="artifact-warnings"><li>Load a frame to analyze artifacts.</li></ul>
        </section>
        <section class="markers-summary" aria-label="Tracking marker summary">
          <div class="panel-title-row">
            <h3>Tracking Markers</h3>
            <label class="overlay-toggle">
              <input id="marker-overlay-toggle" type="checkbox" checked />
              Overlay
            </label>
          </div>
          <div class="quality-grid">
            <div><small>Marker count</small><strong id="marker-count">—</strong></div>
            <div><small>Distribution</small><strong id="marker-distribution">—</strong></div>
            <div class="wide"><small>Distribution score</small><strong id="marker-distribution-score">—</strong></div>
            <div><small>Candidates</small><strong id="marker-candidates">—</strong></div>
            <div><small>After filtering</small><strong id="marker-filtered-count">—</strong></div>
            <div class="wide"><small>Processing time</small><strong id="marker-processing-time">—</strong></div>
          </div>
          <h3>Marker list</h3>
          <ul id="detected-markers"><li>Load a frame to detect tracking markers.</li></ul>
          <h3>Warnings</h3>
          <ul id="marker-warnings"><li>Load a frame to analyze marker distribution.</li></ul>
          <div class="json-label">RAW MARKER DETECTION JSON</div>
          <pre id="raw-marker-output">{ "status": "waiting_for_frame" }</pre>
        </section>
        <section class="motion-summary" aria-label="Optical flow and motion summary">
          <div class="panel-title-row">
            <h3>Optical Flow / Motion</h3>
            <div class="toggle-group">
              <label class="overlay-toggle">
                <input id="motion-enabled-toggle" type="checkbox" checked />
                Analyzer
              </label>
              <label class="overlay-toggle">
                <input id="motion-overlay-toggle" type="checkbox" checked />
                Vectors
              </label>
            </div>
          </div>
          <div class="quality-grid">
            <div class="wide"><small>Possible camera movement</small><strong id="camera-movement">—</strong></div>
            <div><small>Motion magnitude</small><strong id="motion-magnitude">—</strong></div>
            <div><small>Direction</small><strong id="motion-direction">—</strong></div>
            <div><small>Shake score</small><strong id="shake-score">—</strong></div>
            <div><small>Motion stability</small><strong id="motion-stability">—</strong></div>
            <div class="wide"><small>Processing time</small><strong id="motion-processing-time">—</strong></div>
          </div>
          <h3>Warnings</h3>
          <ul id="motion-warnings"><li>Playback is needed to estimate motion.</li></ul>
        </section>
        <section class="report-export" aria-label="JSON report export">
          <div class="panel-title-row">
            <h3>JSON Report Export</h3>
            <span id="report-frame-count">0 frames</span>
          </div>
          <div class="report-scope">
            <label><input type="radio" name="report-scope" value="session" checked /> Full session</label>
            <label><input type="radio" name="report-scope" value="current" /> Current frame only</label>
          </div>
          <button id="export-report" type="button" disabled>Export Limonade EYE Report</button>
          <a id="download-report-link" class="download-link" hidden>Download latest JSON</a>
          <div class="json-label">REPORT PREVIEW</div>
          <pre id="report-preview">{ "status": "waiting_for_frames" }</pre>
        </section>
        <div class="json-label">RAW FRAME ANALYZER JSON</div>
        <pre id="raw-output">{ "status": "waiting_for_frame" }</pre>
        <details>
          <summary>Full session report</summary>
          <pre id="output"></pre>
        </details>
      </aside>
      </section>
    </section>
  </main>`;

mountPCEPlayground(document.querySelector<HTMLElement>('#pce-playground')!);

const chromaTab = document.querySelector<HTMLButtonElement>('#chroma-tab')!;
const pceTab = document.querySelector<HTMLButtonElement>('#pce-tab')!;
const motionLinkTab = document.querySelector<HTMLButtonElement>('#motionlink-tab')!;
const chromaView = document.querySelector<HTMLElement>('#chroma-view')!;
const pceView = document.querySelector<HTMLElement>('#pce-view')!;
const motionLinkView = document.querySelector<HTMLElement>('#motionlink-view')!;

function activateTool(tool: 'chroma' | 'pce' | 'motionlink', updateHash = true): void {
  const showPCE = tool === 'pce';
  const showMotionLink = tool === 'motionlink';
  chromaView.hidden = showPCE || showMotionLink;
  pceView.hidden = !showPCE;
  motionLinkView.hidden = !showMotionLink;
  chromaTab.classList.toggle('active', !showPCE && !showMotionLink);
  pceTab.classList.toggle('active', showPCE);
  motionLinkTab.classList.toggle('active', showMotionLink);
  chromaTab.setAttribute('aria-selected', String(!showPCE && !showMotionLink));
  pceTab.setAttribute('aria-selected', String(showPCE));
  motionLinkTab.setAttribute('aria-selected', String(showMotionLink));
  if (updateHash) history.replaceState(null, '', `${location.pathname}${location.search}#${tool === 'chroma' ? 'vision' : tool}`);
}

chromaTab.addEventListener('click', () => activateTool('chroma'));
pceTab.addEventListener('click', () => activateTool('pce'));
motionLinkTab.addEventListener('click', () => activateTool('motionlink'));
window.addEventListener('hashchange', () => {
  activateTool(
    location.hash === '#pce' ? 'pce' : location.hash === '#motionlink' ? 'motionlink' : 'chroma',
    false,
  );
});
activateTool(
  location.hash === '#pce' ? 'pce' : location.hash === '#motionlink' ? 'motionlink' : 'chroma',
  false,
);
if (location.hash === '#chroma') {
  history.replaceState(null, '', `${location.pathname}${location.search}#vision`);
}

const videoInput = document.querySelector<HTMLInputElement>('#video-file')!;
const video = document.querySelector<HTMLVideoElement>('#video')!;
const canvas = document.querySelector<HTMLCanvasElement>('#frame-canvas')!;
const overlayCanvas = document.querySelector<HTMLCanvasElement>('#overlay-canvas')!;
const analyzeButton = document.querySelector<HTMLButtonElement>('#analyze')!;
const output = document.querySelector<HTMLElement>('#output')!;
const status = document.querySelector<HTMLElement>('#status')!;
const emptyState = document.querySelector<HTMLElement>('#empty-state')!;
const screenType = document.querySelector<HTMLElement>('#screen-type')!;
const qualityScore = document.querySelector<HTMLElement>('#quality-score')!;
const coverage = document.querySelector<HTMLElement>('#coverage')!;
const uniformity = document.querySelector<HTMLElement>('#uniformity')!;
const frameNumber = document.querySelector<HTMLElement>('#frame-number')!;
const frameTimestamp = document.querySelector<HTMLElement>('#frame-timestamp')!;
const analysisTime = document.querySelector<HTMLElement>('#analysis-time')!;
const performanceModeSelect = document.querySelector<HTMLSelectElement>('#performance-mode')!;
const analyzerTimings = document.querySelector<HTMLUListElement>('#analyzer-timings')!;
const analyzerStatus = document.querySelector<HTMLUListElement>('#analyzer-status')!;
const qualityWarnings = document.querySelector<HTMLUListElement>('#quality-warnings')!;
const screenOverlayToggle = document.querySelector<HTMLInputElement>('#screen-overlay-toggle')!;
const spillRisk = document.querySelector<HTMLElement>('#spill-risk')!;
const spillScore = document.querySelector<HTMLElement>('#spill-score')!;
const spillArea = document.querySelector<HTMLElement>('#spill-area')!;
const spillWarnings = document.querySelector<HTMLUListElement>('#spill-warnings')!;
const spillOverlayToggle = document.querySelector<HTMLInputElement>('#spill-overlay-toggle')!;
const lightingOverlayToggle = document.querySelector<HTMLInputElement>('#lighting-overlay-toggle')!;
const exposureOverlayToggle = document.querySelector<HTMLInputElement>('#exposure-overlay-toggle')!;
const edgeOverlayToggle = document.querySelector<HTMLInputElement>('#edge-overlay-toggle')!;
const overlayLegend = document.querySelector<HTMLElement>('#overlay-legend')!;
const overlayRenderStatus = document.querySelector<HTMLElement>('#overlay-render-status')!;
const lightingPattern = document.querySelector<HTMLElement>('#lighting-pattern')!;
const dominantLight = document.querySelector<HTMLElement>('#dominant-light')!;
const lightingBalance = document.querySelector<HTMLElement>('#lighting-balance')!;
const lightingRatio = document.querySelector<HTMLElement>('#lighting-ratio')!;
const detectedLights = document.querySelector<HTMLUListElement>('#detected-lights')!;
const lightingWarnings = document.querySelector<HTMLUListElement>('#lighting-warnings')!;
const screenLightType = document.querySelector<HTMLElement>('#screen-light-type')!;
const screenLightUniformity = document.querySelector<HTMLElement>('#screen-light-uniformity')!;
const screenLightCount = document.querySelector<HTMLElement>('#screen-light-count')!;
const exposureStatus = document.querySelector<HTMLElement>('#exposure-status')!;
const averageLuma = document.querySelector<HTMLElement>('#average-luma')!;
const contrastScore = document.querySelector<HTMLElement>('#contrast-score')!;
const highlightClip = document.querySelector<HTMLElement>('#highlight-clip')!;
const shadowClip = document.querySelector<HTMLElement>('#shadow-clip')!;
const exposureWarnings = document.querySelector<HTMLUListElement>('#exposure-warnings')!;
const lumaHistogram = document.querySelector<HTMLCanvasElement>('#luma-histogram')!;
const edgeScore = document.querySelector<HTMLElement>('#edge-score')!;
const weakEdges = document.querySelector<HTMLElement>('#weak-edges')!;
const chromaSeparation = document.querySelector<HTMLElement>('#chroma-separation')!;
const edgeWarnings = document.querySelector<HTMLUListElement>('#edge-warnings')!;
const artifactScore = document.querySelector<HTMLElement>('#artifact-score')!;
const artifactRisk = document.querySelector<HTMLElement>('#artifact-risk')!;
const detectedArtifacts = document.querySelector<HTMLUListElement>('#detected-artifacts')!;
const artifactWarnings = document.querySelector<HTMLUListElement>('#artifact-warnings')!;
const artifactOverlayToggle = document.querySelector<HTMLInputElement>('#artifact-overlay-toggle')!;
const markerCount = document.querySelector<HTMLElement>('#marker-count')!;
const markerDistribution = document.querySelector<HTMLElement>('#marker-distribution')!;
const markerDistributionScore = document.querySelector<HTMLElement>('#marker-distribution-score')!;
const markerCandidates = document.querySelector<HTMLElement>('#marker-candidates')!;
const markerFilteredCount = document.querySelector<HTMLElement>('#marker-filtered-count')!;
const markerProcessingTime = document.querySelector<HTMLElement>('#marker-processing-time')!;
const detectedMarkers = document.querySelector<HTMLUListElement>('#detected-markers')!;
const markerWarnings = document.querySelector<HTMLUListElement>('#marker-warnings')!;
const markerOverlayToggle = document.querySelector<HTMLInputElement>('#marker-overlay-toggle')!;
const rawMarkerOutput = document.querySelector<HTMLElement>('#raw-marker-output')!;
const cameraMovement = document.querySelector<HTMLElement>('#camera-movement')!;
const motionMagnitude = document.querySelector<HTMLElement>('#motion-magnitude')!;
const motionDirection = document.querySelector<HTMLElement>('#motion-direction')!;
const shakeScore = document.querySelector<HTMLElement>('#shake-score')!;
const motionStability = document.querySelector<HTMLElement>('#motion-stability')!;
const motionProcessingTime = document.querySelector<HTMLElement>('#motion-processing-time')!;
const motionWarnings = document.querySelector<HTMLUListElement>('#motion-warnings')!;
const motionEnabledToggle = document.querySelector<HTMLInputElement>('#motion-enabled-toggle')!;
const motionOverlayToggle = document.querySelector<HTMLInputElement>('#motion-overlay-toggle')!;
const rawOutput = document.querySelector<HTMLElement>('#raw-output')!;
const exportReportButton = document.querySelector<HTMLButtonElement>('#export-report')!;
const downloadReportLink = document.querySelector<HTMLAnchorElement>('#download-report-link')!;
const reportPreview = document.querySelector<HTMLElement>('#report-preview')!;
const reportFrameCount = document.querySelector<HTMLElement>('#report-frame-count')!;
const motionLinkFileInput = document.querySelector<HTMLInputElement>('#motionlink-file')!;
const motionLinkFileName = document.querySelector<HTMLElement>('#motionlink-file-name')!;
const motionLinkJsonPaste = document.querySelector<HTMLTextAreaElement>('#motionlink-json-paste')!;
const motionLinkLoadPaste = document.querySelector<HTMLButtonElement>('#motionlink-load-paste')!;
const motionLinkLoadSample = document.querySelector<HTMLButtonElement>('#motionlink-load-sample')!;
const motionLinkUseOpticalFlow = document.querySelector<HTMLInputElement>('#motionlink-use-optical-flow')!;
const motionLinkCleanEnabled = document.querySelector<HTMLInputElement>('#motionlink-clean-enabled')!;
const motionLinkCleanStrength = document.querySelector<HTMLSelectElement>('#motionlink-clean-strength')!;
const motionLinkRawDistance = document.querySelector<HTMLElement>('#motionlink-raw-distance')!;
const motionLinkCleanDistance = document.querySelector<HTMLElement>('#motionlink-clean-distance')!;
const motionLinkCleanAdjusted = document.querySelector<HTMLElement>('#motionlink-clean-adjusted')!;
const motionLinkVisualizerCanvas = document.querySelector<HTMLCanvasElement>('#motionlink-visualizer-canvas')!;
const motionLinkPlayToggle = document.querySelector<HTMLButtonElement>('#motionlink-play-toggle')!;
const motionLinkRestart = document.querySelector<HTMLButtonElement>('#motionlink-restart')!;
const motionLinkPlaybackSpeed = document.querySelector<HTMLSelectElement>('#motionlink-playback-speed')!;
const motionLinkExportSnapshot = document.querySelector<HTMLButtonElement>('#motionlink-export-snapshot')!;
const motionLinkTimeline = document.querySelector<HTMLInputElement>('#motionlink-timeline')!;
const motionLinkTimeReadout = document.querySelector<HTMLElement>('#motionlink-time-readout')!;
const motionLinkPathMode = document.querySelector<HTMLSelectElement>('#motionlink-path-mode')!;
const motionLinkAnimateSourceToggle = document.querySelector<HTMLButtonElement>('#motionlink-animate-source')!;
const motionLinkShowGrid = document.querySelector<HTMLInputElement>('#motionlink-show-grid')!;
const motionLinkShowPath = document.querySelector<HTMLInputElement>('#motionlink-show-path')!;
const motionLinkShowKeyframes = document.querySelector<HTMLInputElement>('#motionlink-show-keyframes')!;
const motionLinkShowConfidence = document.querySelector<HTMLInputElement>('#motionlink-show-confidence')!;
const motionLinkSchemaStatus = document.querySelector<HTMLElement>('#motionlink-schema-status')!;
const motionLinkContinuity = document.querySelector<HTMLElement>('#motionlink-continuity')!;
const motionLinkDroppedSamples = document.querySelector<HTMLElement>('#motionlink-dropped-samples')!;
const motionLinkTimestampGaps = document.querySelector<HTMLElement>('#motionlink-timestamp-gaps')!;
const motionLinkDistance = document.querySelector<HTMLElement>('#motionlink-distance')!;
const motionLinkAverageConfidence = document.querySelector<HTMLElement>('#motionlink-average-confidence')!;
const motionLinkMaxJitter = document.querySelector<HTMLElement>('#motionlink-max-jitter')!;
const motionLinkPositionState = document.querySelector<HTMLElement>('#motionlink-position-state')!;
const motionLinkSessionId = document.querySelector<HTMLElement>('#motionlink-session-id')!;
const motionLinkDuration = document.querySelector<HTMLElement>('#motionlink-duration')!;
const motionLinkSampleCount = document.querySelector<HTMLElement>('#motionlink-sample-count')!;
const motionLinkEstimateCount = document.querySelector<HTMLElement>('#motionlink-estimate-count')!;
const motionLinkDominantMotion = document.querySelector<HTMLElement>('#motionlink-dominant-motion')!;
const motionLinkConfidence = document.querySelector<HTMLElement>('#motionlink-confidence')!;
const motionLinkRawCleanedCopy = document.querySelector<HTMLElement>('#motionlink-raw-cleaned-copy')!;
const motionLinkRawMetrics = document.querySelector<HTMLElement>('#motionlink-raw-metrics')!;
const motionLinkCleanedMetrics = document.querySelector<HTMLElement>('#motionlink-cleaned-metrics')!;
const motionLinkMovementLabels = document.querySelector<HTMLUListElement>('#motionlink-movement-labels')!;
const motionLinkOrientationGraph = document.querySelector<HTMLCanvasElement>('#motionlink-orientation-graph')!;
const motionLinkProcessedPrompt = document.querySelector<HTMLElement>('#motionlink-processed-prompt')!;
const motionLinkDownloadProcessed = document.querySelector<HTMLButtonElement>('#motionlink-download-processed')!;
const motionLinkDownloadUsd = document.querySelector<HTMLButtonElement>('#motionlink-download-usd')!;
const motionLinkDownloadFbx = document.querySelector<HTMLButtonElement>('#motionlink-download-fbx')!;
const motionLinkProcessorStatus = document.querySelector<HTMLElement>('#motionlink-processor-status')!;
const motionLinkAuditScore = document.querySelector<HTMLElement>('#motionlink-audit-score')!;
const motionLinkAuditDirection = document.querySelector<HTMLElement>('#motionlink-audit-direction')!;
const motionLinkAuditActual = document.querySelector<HTMLElement>('#motionlink-audit-actual')!;
const motionLinkAuditDisagreement = document.querySelector<HTMLElement>('#motionlink-audit-disagreement')!;
const motionLinkAuditList = document.querySelector<HTMLUListElement>('#motionlink-audit-list')!;
const motionLinkSegmentList = document.querySelector<HTMLUListElement>('#motionlink-segment-list')!;
const motionLinkWarningList = document.querySelector<HTMLUListElement>('#motionlink-warning-list')!;
const motionLinkRawOutput = document.querySelector<HTMLElement>('#motionlink-raw-output')!;
const context = canvas.getContext('2d', { willReadFrequently: true })!;
const overlayContext = overlayCanvas.getContext('2d')!;
const histogramContext = lumaHistogram.getContext('2d')!;
const motionLinkVisualizerContext = motionLinkVisualizerCanvas.getContext('2d')!;
const motionLinkOrientationGraphContext = motionLinkOrientationGraph.getContext('2d')!;
if (!context) throw new Error('A 2D canvas context is required.');
if (!overlayContext) throw new Error('A 2D overlay canvas context is required.');
if (!histogramContext) throw new Error('A 2D histogram canvas context is required.');
if (!motionLinkVisualizerContext) throw new Error('A 2D MotionLink visualizer context is required.');
if (!motionLinkOrientationGraphContext) throw new Error('A 2D MotionLink orientation graph context is required.');

const motionAnalyzer = new OpticalFlowAnalyzer();
const engine = new LimonadeEyeEngine([
  new ScreenQualityAnalyzer(),
  new SpillDetectionAnalyzer(),
  new MultiSourceLightingAnalyzer(),
  new ExposureAnalyzer(),
  new VideoArtifactsAnalyzer(),
  new EdgeConfidenceAnalyzer(),
  new TrackingMarkerAnalyzer(),
  motionAnalyzer,
]);
const frames: LimonadeEyeFrameResult[] = [];
const jsonExporter = new LimonadeEyeJSONExporter();
const motionLinkProcessor = new MotionLinkProcessor();
const motionLinkJsonLoader = new MotionLinkJsonLoader();
const motionLinkVisualizer = new MotionLinkVisualizerCanvas(motionLinkVisualizerCanvas, motionLinkVisualizerContext);
let reportDownloadUrl: string | undefined;
let motionLinkDownloadUrl: string | undefined;
let lastMotionLinkResult: MotionLinkProcessorResult | undefined;
let lastMotionLinkFileText: string | undefined;
let lastMotionLinkLoadResult: MotionLinkJsonLoadResult | undefined;
let lastMotionLinkCleaningStats: MotionLinkCleaningStats | undefined;
let motionLinkIsPlaying = false;
let motionLinkAnimateSource: MotionLinkAnimateSource = 'cleaned';
let reportSessionId = globalThis.crypto?.randomUUID?.() ?? `eye-${Date.now()}`;
let reportCreatedAt = new Date().toISOString();
let videoUrl: string | undefined;
let selectedFile: File | undefined;
let analysisInFlight = false;
let playbackTimer: number | undefined;
let lastFrameResult: LimonadeEyeFrameResult | undefined;
const selfTestMode = new URLSearchParams(window.location.search).get('selftest') === '1';
const playbackCadenceMs: Record<LimonadeEyePerformanceMode, number> = {
  quality: 700,
  balanced: 500,
  realtime: 250,
};

function currentPerformanceMode(): LimonadeEyePerformanceMode {
  return performanceModeSelect.value as LimonadeEyePerformanceMode;
}

function updateAnalyzerStatus(): void {
  analyzerStatus.replaceChildren();
  const enabledIds = new Set(engine.listAnalyzers().map((analyzer) => analyzer.id));
  const mode = currentPerformanceMode();
  const modeCadence: Record<LimonadeEyePerformanceMode, Record<string, number>> = {
    quality: {},
    balanced: { 'video-artifacts': 2, 'tracking-markers': 2 },
    realtime: { 'lighting-setup': 2, 'video-artifacts': 4, 'edge-confidence': 2, 'tracking-markers': 4 },
  };
  [
    'screen-quality', 'spill-detection', 'lighting-setup', 'exposure',
    'video-artifacts', 'edge-confidence', 'tracking-markers', 'optical-flow',
  ].forEach((id) => {
    const enabled = enabledIds.has(id);
    const cadence = modeCadence[mode][id] ?? 1;
    const item = document.createElement('li');
    item.textContent = `${id} — ${enabled ? `enabled · every ${cadence} frame${cadence === 1 ? '' : 's'}` : 'disabled'}`;
    item.dataset.state = enabled ? 'enabled' : 'disabled';
    analyzerStatus.append(item);
  });
}

updateAnalyzerStatus();

function selectedReportFrames(): LimonadeEyeFrameResult[] {
  const scope = document.querySelector<HTMLInputElement>('input[name="report-scope"]:checked')?.value;
  return scope === 'current' && lastFrameResult ? [lastFrameResult] : [...frames];
}

function reportOptions() {
  return {
    sessionId: reportSessionId,
    createdAt: reportCreatedAt,
    source: {
      type: 'video' as const,
      name: selectedFile?.name ?? (selfTestMode ? 'limonade-eye-self-test.webm' : null),
      width: lastFrameResult?.width ?? (video.videoWidth || null),
      height: lastFrameResult?.height ?? (video.videoHeight || null),
      fps: null,
      duration_ms: Number.isFinite(video.duration) ? video.duration * 1000 : null,
    },
  };
}

function currentReport() {
  return createLimonadeEyeReport(selectedReportFrames(), reportOptions());
}

function updateReportPreview(): void {
  const report = currentReport();
  const json = JSON.stringify(report, null, 2);
  reportPreview.textContent = json;
  reportFrameCount.textContent = `${report.frames.length} frame${report.frames.length === 1 ? '' : 's'}`;
  exportReportButton.disabled = report.frames.length === 0;
  if (reportDownloadUrl) URL.revokeObjectURL(reportDownloadUrl);
  if (report.frames.length === 0) {
    downloadReportLink.hidden = true;
    downloadReportLink.removeAttribute('href');
    return;
  }
  reportDownloadUrl = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
  const scope = report.frames.length === 1 ? `frame-${report.frames[0]!.frame_number}` : 'session';
  downloadReportLink.href = reportDownloadUrl;
  downloadReportLink.download = `limonade-eye-${scope}-${reportSessionId}.json`;
  downloadReportLink.hidden = false;
}

output.textContent = JSON.stringify(currentReport(), null, 2);
updateReportPreview();

function formatCompactMs(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  if (value < 1000) return `${value.toFixed(0)} ms`;
  return `${(value / 1000).toFixed(2)} s`;
}

function formatMotionLabel(value: string | null | undefined): string {
  return value ? value.replaceAll('_', ' ').toUpperCase() : '—';
}

function formatMeters(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${value.toFixed(value < 1 ? 3 : 2)} m`;
}

function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${Math.round(value * 100)}%`;
}

function formatRisk(value: string | null | undefined): string {
  return value ? value.replaceAll('_', ' ').toUpperCase() : '—';
}

function renderMotionLinkMetricList(container: HTMLElement, metrics: MotionLinkComparisonMetrics): void {
  const rows = [
    ['Sample count', String(metrics.sampleCount)],
    ['Duration', formatCompactMs(metrics.durationMs)],
    ['Estimated distance', formatMeters(metrics.estimatedDistance)],
    ['Jitter score', formatPercent(metrics.jitterScore)],
    ['Shake score', formatPercent(metrics.shakeScore)],
    ['Drift risk', formatRisk(metrics.driftRisk)],
    ['Smoothing amount', formatPercent(metrics.smoothingAmount)],
    ['Confidence', metrics.confidence === null ? '—' : formatPercent(metrics.confidence)],
    ['Dropped samples', String(metrics.droppedSamples)],
    ['Timestamp gaps', String(metrics.timestampGaps)],
  ];
  container.replaceChildren();
  rows.forEach(([label, value]) => {
    const item = document.createElement('div');
    item.innerHTML = `<small>${label}</small><strong>${value}</strong>`;
    container.append(item);
  });
}

function resizeGraphCanvas(canvas: HTMLCanvasElement, context: CanvasRenderingContext2D): { width: number; height: number } {
  const rect = canvas.getBoundingClientRect();
  const width = Math.max(320, Math.round(rect.width || 560));
  const height = Math.max(170, Math.round(rect.height || 210));
  const scale = window.devicePixelRatio || 1;
  const targetWidth = Math.round(width * scale);
  const targetHeight = Math.round(height * scale);
  if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
    canvas.width = targetWidth;
    canvas.height = targetHeight;
  }
  context.setTransform(scale, 0, 0, scale, 0, 0);
  return { width, height };
}

function renderMotionLinkOrientationGraph(graph: MotionLinkOrientationGraphData): void {
  const { width, height } = resizeGraphCanvas(motionLinkOrientationGraph, motionLinkOrientationGraphContext);
  motionLinkOrientationGraphContext.clearRect(0, 0, width, height);
  motionLinkOrientationGraphContext.fillStyle = '#050706';
  motionLinkOrientationGraphContext.fillRect(0, 0, width, height);
  motionLinkOrientationGraphContext.strokeStyle = '#dfff5718';
  motionLinkOrientationGraphContext.lineWidth = 1;
  for (let index = 0; index <= 4; index++) {
    const y = 18 + (height - 42) * (index / 4);
    motionLinkOrientationGraphContext.beginPath();
    motionLinkOrientationGraphContext.moveTo(12, y);
    motionLinkOrientationGraphContext.lineTo(width - 12, y);
    motionLinkOrientationGraphContext.stroke();
  }

  if (!graph.hasOrientation || graph.points.length < 2) {
    motionLinkOrientationGraphContext.fillStyle = '#7f897f';
    motionLinkOrientationGraphContext.font = '700 12px Inter, sans-serif';
    motionLinkOrientationGraphContext.textAlign = 'center';
    motionLinkOrientationGraphContext.fillText('No yaw / pitch / roll samples available', width / 2, height / 2);
    return;
  }

  const allValues = graph.points.flatMap((point) => [point.yaw, point.pitch, point.roll]);
  const maxAbs = Math.max(0.08, ...allValues.map((value) => Math.abs(value)));
  const startTime = graph.points[0]!.timestampMs;
  const endTime = graph.points[graph.points.length - 1]!.timestampMs;
  const timeSpan = Math.max(1, endTime - startTime);
  const xFor = (timestampMs: number) => 18 + ((timestampMs - startTime) / timeSpan) * (width - 36);
  const yFor = (value: number) => height / 2 - (value / maxAbs) * (height * 0.38);
  const drawSeries = (key: 'yaw' | 'pitch' | 'roll', color: string) => {
    motionLinkOrientationGraphContext.beginPath();
    graph.points.forEach((point, index) => {
      const x = xFor(point.timestampMs);
      const y = yFor(point[key]);
      if (index === 0) motionLinkOrientationGraphContext.moveTo(x, y);
      else motionLinkOrientationGraphContext.lineTo(x, y);
    });
    motionLinkOrientationGraphContext.strokeStyle = color;
    motionLinkOrientationGraphContext.lineWidth = 2;
    motionLinkOrientationGraphContext.stroke();
  };
  drawSeries('yaw', '#55dfff');
  drawSeries('pitch', '#dfff57');
  drawSeries('roll', '#ff9f43');
  motionLinkOrientationGraphContext.fillStyle = '#cbd5c6';
  motionLinkOrientationGraphContext.font = '10px ui-monospace, SFMono-Regular, monospace';
  motionLinkOrientationGraphContext.textAlign = 'left';
  motionLinkOrientationGraphContext.fillText(`±${(maxAbs * 180 / Math.PI).toFixed(1)}°`, 14, 16);
  motionLinkOrientationGraphContext.fillStyle = '#55dfff';
  motionLinkOrientationGraphContext.fillText('yaw', width - 104, 16);
  motionLinkOrientationGraphContext.fillStyle = '#dfff57';
  motionLinkOrientationGraphContext.fillText('pitch', width - 72, 16);
  motionLinkOrientationGraphContext.fillStyle = '#ff9f43';
  motionLinkOrientationGraphContext.fillText('roll', width - 31, 16);
}

function renderMotionLinkRawVsCleanedAnalysis(analysis: MotionLinkRawVsCleanedAnalysis): void {
  motionLinkRawCleanedCopy.textContent = analysis.copy;
  renderMotionLinkMetricList(motionLinkRawMetrics, analysis.raw);
  renderMotionLinkMetricList(motionLinkCleanedMetrics, analysis.cleaned);
  renderMotionLinkOrientationGraph(analysis.orientationGraph);

  motionLinkMovementLabels.replaceChildren();
  if (!analysis.movementLabels.length) {
    const item = document.createElement('li');
    item.textContent = 'No strong cleaned-path movement labels detected yet.';
    motionLinkMovementLabels.append(item);
  } else {
    analysis.movementLabels.forEach((label) => {
      const item = document.createElement('li');
      item.innerHTML = `
        <strong>${formatMotionLabel(label.type)}</strong>
        <span>${label.reason}</span>
        <em>confidence ${Math.round(label.confidence * 100)}%</em>
      `;
      motionLinkMovementLabels.append(item);
    });
  }
}

function averageEstimateConfidence(result: MotionLinkProcessorResult): number {
  if (!result.estimates.length) return 0;
  return result.estimates.reduce((sum, estimate) => sum + estimate.confidence, 0) / result.estimates.length;
}

function currentOpticalFlowResults(): OpticalFlowResult[] {
  return frames.flatMap((frame) =>
    frame.analyzers.filter((analyzer): analyzer is OpticalFlowResult => analyzer.module_id === 'optical-flow'),
  );
}

function motionLinkDurationMs(result: MotionLinkProcessorResult): number | null {
  const timestamps = result.estimates.map((estimate) => estimate.timestamp_ms);
  if (timestamps.length < 2) return null;
  return Math.max(...timestamps) - Math.min(...timestamps);
}

function motionLinkRawSessionId(raw: unknown): string | null {
  if (typeof raw !== 'object' || raw === null || !('session_id' in raw)) return null;
  const value = (raw as { session_id?: unknown }).session_id;
  return typeof value === 'string' ? value : null;
}

function setMotionLinkDownload(result: MotionLinkProcessorResult): void {
  if (motionLinkDownloadUrl) URL.revokeObjectURL(motionLinkDownloadUrl);
  const json = JSON.stringify(result, null, 2);
  motionLinkDownloadUrl = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
  motionLinkDownloadProcessed.disabled = false;
  motionLinkDownloadUsd.disabled = false;
  motionLinkDownloadFbx.disabled = false;
}

function downloadTextFile(fileName: string, text: string, type: string): void {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function renderMotionLinkAIVideoAudit(result: MotionLinkProcessorResult): void {
  motionLinkAuditList.replaceChildren();
  if (!result.optical_flow_comparisons.length) {
    motionLinkAuditScore.textContent = '—';
    motionLinkAuditDirection.textContent = '—';
    motionLinkAuditActual.textContent = '—';
    motionLinkAuditDisagreement.textContent = '—';
    const item = document.createElement('li');
    item.textContent = 'No Optical Flow comparisons yet. Analyze the generated video in Vision Analysis, then reload the MotionLink JSON.';
    motionLinkAuditList.append(item);
    return;
  }

  const audit = createMotionLinkAIVideoAudit(result);
  motionLinkAuditScore.textContent = `${Math.round(audit.prompt_adherence_score * 100)}%`;
  motionLinkAuditDirection.textContent = `${Math.round(audit.direction_agreement_score * 100)}%`;
  motionLinkAuditActual.textContent = formatMotionLabel(audit.actual_visual_motion.dominant_type);
  motionLinkAuditDisagreement.textContent = `${Math.round(audit.disagreement_rate * 100)}%`;

  const messages = [
    ...audit.missing_motion,
    ...audit.extra_motion,
    ...audit.recommendations,
    ...audit.warnings,
  ];
  if (!messages.length) {
    const item = document.createElement('li');
    item.className = 'clear';
    item.textContent = 'Generated video motion broadly matches the MotionLink prompt.';
    motionLinkAuditList.append(item);
    return;
  }
  [...new Set(messages)].slice(0, 8).forEach((message) => {
    const item = document.createElement('li');
    item.textContent = message;
    motionLinkAuditList.append(item);
  });
}

function setMotionLinkPlaybackEnabled(enabled: boolean, durationMs: number): void {
  motionLinkPlayToggle.disabled = !enabled;
  motionLinkRestart.disabled = !enabled;
  motionLinkExportSnapshot.disabled = !enabled;
  motionLinkTimeline.disabled = !enabled;
  motionLinkTimeline.max = String(Math.max(0, Math.round(durationMs)));
  motionLinkTimeline.value = '0';
  motionLinkTimeReadout.textContent = `0 ms / ${Math.round(durationMs)} ms`;
}

function currentMotionLinkCleaningOptions(): MotionLinkCleaningOptions {
  return {
    enabled: motionLinkCleanEnabled.checked,
    strength: motionLinkCleanStrength.value as MotionLinkCleaningOptions['strength'],
  };
}

function processorSmoothingWindow(): number {
  if (!motionLinkCleanEnabled.checked) return 3;
  if (motionLinkCleanStrength.value === 'strong') return 9;
  if (motionLinkCleanStrength.value === 'light') return 3;
  return 5;
}

function updateMotionLinkVisualizerOptions(): void {
  const state = normalizeMotionLinkVisualizerState({
    pathMode: motionLinkPathMode.value,
    animateSource: motionLinkAnimateSource,
  });
  motionLinkAnimateSource = state.animateSource;
  motionLinkPathMode.value = state.pathMode;
  motionLinkAnimateSourceToggle.textContent = state.animateSource === 'raw' ? 'Animate cleaned path' : 'Animate raw path';
  motionLinkVisualizer.setOptions({
    showGrid: motionLinkShowGrid.checked,
    showPath: motionLinkShowPath.checked,
    showKeyframes: motionLinkShowKeyframes.checked,
    showConfidence: motionLinkShowConfidence.checked,
    pathMode: state.pathMode,
    animateSource: state.animateSource,
  });
}

function updateMotionLinkCleaningReadout(stats: MotionLinkCleaningStats | undefined): void {
  if (!stats) {
    motionLinkRawDistance.textContent = '—';
    motionLinkCleanDistance.textContent = '—';
    motionLinkCleanAdjusted.textContent = '—';
    return;
  }
  const adjusted =
    stats.impossibleJumpSamplesAdjusted +
    stats.outlierSamplesAdjusted +
    stats.deadbandSamplesAdjusted +
    stats.lowConfidenceSamplesAdjusted;
  motionLinkRawDistance.textContent = formatMeters(stats.rawDistance);
  motionLinkCleanDistance.textContent = formatMeters(stats.cleanedDistance);
  motionLinkCleanAdjusted.textContent = String(adjusted);
}

function renderMotionLinkVisualization(loadResult: MotionLinkJsonLoadResult): void {
  lastMotionLinkLoadResult = loadResult;
  const { quality } = loadResult;
  const cleaning = cleanMotionLinkVisualizationData(loadResult.data, currentMotionLinkCleaningOptions());
  lastMotionLinkCleaningStats = cleaning.stats;
  motionLinkVisualizer.setData(cleaning.data, loadResult.data);
  motionLinkAnimateSourceToggle.disabled = !loadResult.data.samples.length;
  updateMotionLinkVisualizerOptions();
  updateMotionLinkCleaningReadout(cleaning.stats);
  renderMotionLinkRawVsCleanedAnalysis(
    createMotionLinkRawVsCleanedAnalysis(loadResult.data, cleaning.data, loadResult.quality, cleaning.stats),
  );
  setMotionLinkPlaybackEnabled(cleaning.data.samples.length > 0, cleaning.data.durationMs);

  motionLinkSchemaStatus.textContent = quality.schemaValid ? 'VALID' : 'INVALID';
  motionLinkContinuity.textContent = quality.sampleContinuity.toUpperCase();
  motionLinkDroppedSamples.textContent = String(quality.droppedSampleCount);
  motionLinkTimestampGaps.textContent = String(quality.timestampGapCount);
  motionLinkDistance.textContent = formatMeters(quality.estimatedDistance);
  motionLinkAverageConfidence.textContent = quality.averageConfidence === null ? '—' : `${Math.round(quality.averageConfidence * 100)}%`;
  motionLinkMaxJitter.textContent = formatMeters(quality.maxJitter);
  motionLinkPositionState.textContent = quality.hasPositionData ? 'PRESENT' : quality.hasRotationData ? 'ORIENTATION ONLY' : 'MISSING';

  if (!lastMotionLinkResult) {
    motionLinkSessionId.textContent = motionLinkRawSessionId(loadResult.raw)?.slice(0, 12) ?? '—';
    motionLinkDuration.textContent = formatCompactMs(cleaning.data.durationMs);
    motionLinkSampleCount.textContent = String(cleaning.data.samples.length);
    motionLinkEstimateCount.textContent = '—';
    motionLinkDominantMotion.textContent = formatMotionLabel(quality.detectedMotionType);
    motionLinkConfidence.textContent = quality.averageConfidence === null ? '—' : `${Math.round(quality.averageConfidence * 100)}%`;
  }
}

function renderMotionLinkResult(result: MotionLinkProcessorResult): void {
  lastMotionLinkResult = result;
  const opticalFlowCount = currentOpticalFlowResults().length;
  const duration = motionLinkDurationMs(result);
  const confidence = averageEstimateConfidence(result);
  const prompt = result.ai_motion_prompt.motion_prompt;

  motionLinkSessionId.textContent = result.session_id.slice(0, 12);
  motionLinkDuration.textContent = formatCompactMs(duration);
  motionLinkSampleCount.textContent = String(result.frame_timestamps_ms.length || result.estimates.length);
  motionLinkEstimateCount.textContent = String(result.estimates.length);
  motionLinkDominantMotion.textContent = formatMotionLabel(result.ai_motion_prompt.camera_movement.type);
  motionLinkConfidence.textContent = `${Math.round(confidence * 100)}%`;
  motionLinkProcessedPrompt.textContent = prompt;
  motionLinkRawOutput.textContent = JSON.stringify(result, null, 2);
  const cleanupText = lastMotionLinkCleaningStats?.enabled
    ? ` · ${lastMotionLinkCleaningStats.strength} cleanup preview`
    : ' · raw preview';
  motionLinkProcessorStatus.textContent = `Processed locally · ${result.motion_segments.length} segment${result.motion_segments.length === 1 ? '' : 's'} · ${opticalFlowCount} optical-flow frame${opticalFlowCount === 1 ? '' : 's'} available${cleanupText}`;
  setMotionLinkDownload(result);
  renderMotionLinkAIVideoAudit(result);

  motionLinkSegmentList.replaceChildren();
  if (!result.motion_segments.length) {
    const item = document.createElement('li');
    item.textContent = 'No motion segments were strong enough to report.';
    motionLinkSegmentList.append(item);
  } else {
    result.motion_segments.forEach((segment) => {
      const item = document.createElement('li');
      item.innerHTML = `
        <strong>${formatMotionLabel(segment.motion_type)}</strong>
        <span>${formatCompactMs(segment.start_ms)} → ${formatCompactMs(segment.end_ms)} · confidence ${Math.round(segment.confidence * 100)}%</span>
        <em>pan ${segment.pan_degrees?.toFixed(1) ?? '—'}° · tilt ${segment.tilt_degrees?.toFixed(1) ?? '—'}° · shake ${Math.round(segment.shake_intensity * 100)}%</em>
      `;
      motionLinkSegmentList.append(item);
    });
  }

  motionLinkWarningList.replaceChildren();
  const visualizerWarnings = lastMotionLinkCleaningStats?.warnings ?? [];
  const combinedWarnings = [
    ...result.warnings.map((warning) => warning.message),
    ...visualizerWarnings,
  ];
  if (!combinedWarnings.length) {
    const item = document.createElement('li');
    item.className = 'clear';
    item.textContent = 'No MotionLink warnings.';
    motionLinkWarningList.append(item);
  } else {
    [...new Set(combinedWarnings)].forEach((message) => {
      const item = document.createElement('li');
      item.textContent = message;
      motionLinkWarningList.append(item);
    });
  }
}

async function processMotionLinkText(text: string, fileName = 'MotionLink JSON'): Promise<void> {
  motionLinkProcessorStatus.textContent = 'Processing MotionLink JSON locally…';
  motionLinkFileName.textContent = fileName;
  lastMotionLinkResult = undefined;
  motionLinkDownloadUsd.disabled = true;
  motionLinkDownloadFbx.disabled = true;
  renderMotionLinkAIVideoAudit({
    schema: 'limonade-motionlink/v1',
    session_id: 'pending',
    frame_timestamps_ms: [],
    estimates: [],
    motion_segments: [],
    optical_flow_comparisons: [],
    ai_motion_prompt: {
      motion_prompt: '',
      camera_movement: {
        type: 'unknown',
        pan_degrees: null,
        tilt_degrees: null,
        roll_degrees: null,
        shake_intensity: 0,
      },
    },
    warnings: [],
  });
  const loadResult = motionLinkJsonLoader.loadText(text);
  renderMotionLinkVisualization(loadResult);
  motionLinkWarningList.replaceChildren();
  loadResult.quality.warnings.forEach((message) => {
    const item = document.createElement('li');
    item.textContent = message;
    motionLinkWarningList.append(item);
  });
  if (!loadResult.quality.warnings.length) {
    const item = document.createElement('li');
    item.className = 'clear';
    item.textContent = 'No visualizer quality warnings.';
    motionLinkWarningList.append(item);
  }

  try {
    const opticalFlowResults = motionLinkUseOpticalFlow.checked ? currentOpticalFlowResults() : [];
    const result = motionLinkProcessor.process(text, {
      smoothing_window: processorSmoothingWindow(),
      optical_flow_results: opticalFlowResults,
    });
    renderMotionLinkResult(result);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    lastMotionLinkResult = undefined;
    motionLinkDownloadProcessed.disabled = true;
    motionLinkDownloadUsd.disabled = true;
    motionLinkDownloadFbx.disabled = true;
    motionLinkProcessorStatus.textContent = loadResult.ok
      ? 'Visualizer loaded; processor could not create fused estimates.'
      : 'MotionLink processing failed.';
    motionLinkRawOutput.textContent = JSON.stringify({
      error: message,
      visualizer: loadResult.data,
      quality: loadResult.quality,
    }, null, 2);
    const item = document.createElement('li');
    item.textContent = message;
    motionLinkWarningList.append(item);
  }
}

motionLinkVisualizer.setTimeUpdateHandler((timeMs) => {
  const duration = Number(motionLinkTimeline.max) || 0;
  motionLinkTimeline.value = String(Math.round(timeMs));
  motionLinkTimeReadout.textContent = `${Math.round(timeMs)} ms / ${Math.round(duration)} ms`;
  if (duration > 0 && timeMs >= duration) {
    motionLinkIsPlaying = false;
    motionLinkPlayToggle.textContent = 'Play';
  }
});
motionLinkVisualizer.render();

function updateQualitySummary(frameResult: LimonadeEyeFrameResult): void {
  const result = frameResult.analyzers.find((analyzer) => analyzer.module_id === 'screen-quality');
  if (!result) return;
  const metrics = result.metrics as ScreenQualityMetrics;
  screenType.textContent = metrics.screen_type.toUpperCase();
  qualityScore.textContent = `${Math.round(metrics.screen_quality_score * 100)}%`;
  coverage.textContent = `${metrics.coverage_percentage.toFixed(1)}%`;
  uniformity.textContent = `${Math.round(metrics.uniformity_score * 100)}%`;
  qualityWarnings.replaceChildren();
  if (!result.warnings.length) {
    const item = document.createElement('li');
    item.className = 'clear';
    item.textContent = 'No screen quality warnings.';
    qualityWarnings.append(item);
    return;
  }
  result.warnings.forEach((warning) => {
    const item = document.createElement('li');
    item.textContent = warning.message;
    qualityWarnings.append(item);
  });
}

function updateDebugInfo(frameResult: LimonadeEyeFrameResult): void {
  frameNumber.textContent = String(frameResult.frame_number);
  frameTimestamp.textContent = `${frameResult.timestamp_ms.toFixed(1)} ms`;
  analysisTime.textContent = `${frameResult.processing_duration_ms.toFixed(2)} ms`;
  analyzerTimings.replaceChildren();
  engine.listAnalyzers().forEach((analyzer) => {
    const item = document.createElement('li');
    const timing = frameResult.analyzer_timings_ms[analyzer.id];
    item.textContent = `${analyzer.id}: ${timing?.toFixed(2) ?? '—'} ms`;
    analyzerTimings.append(item);
  });
  rawOutput.textContent = JSON.stringify(frameResult, null, 2);
}

function updateSpillSummary(frameResult: LimonadeEyeFrameResult): void {
  const result = frameResult.analyzers.find((analyzer) => analyzer.module_id === 'spill-detection');
  if (!result) return;
  const metrics = result.metrics as SpillDetectionMetrics;
  spillRisk.textContent = metrics.spill_risk.toUpperCase();
  spillScore.textContent = `${Math.round(metrics.spill_score * 100)}%`;
  spillArea.textContent = `${metrics.affected_area_percentage.toFixed(2)}%`;
  spillWarnings.replaceChildren();
  if (!result.warnings.length) {
    const item = document.createElement('li');
    item.className = 'clear';
    item.textContent = 'No spill warnings.';
    spillWarnings.append(item);
    return;
  }
  result.warnings.forEach((warning) => {
    const item = document.createElement('li');
    item.textContent = warning.message;
    spillWarnings.append(item);
  });
}

function updateLightingSummary(frameResult: LimonadeEyeFrameResult): void {
  const result = frameResult.analyzers.find((analyzer) => analyzer.module_id === 'lighting-setup');
  if (!result) return;
  const metrics = result.metrics as LightingSetupMetrics;
  lightingPattern.textContent = metrics.lighting_pattern.replaceAll('_', ' ').toUpperCase();
  dominantLight.textContent = metrics.dominant_light_source
    ? `APPARENT ${metrics.dominant_light_source.role.toUpperCase()} · ${metrics.dominant_light_source.direction.replaceAll('_', ' ').toUpperCase()}`
    : 'UNKNOWN';
  lightingBalance.textContent = `${Math.round(metrics.lighting_balance_score * 100)}%`;
  lightingRatio.textContent = metrics.lighting_ratio === null ? '—' : `${metrics.lighting_ratio.toFixed(2)}:1`;
  detectedLights.replaceChildren();
  if (!metrics.detected_light_sources.length) {
    const item = document.createElement('li');
    item.textContent = 'No apparent light sources could be estimated.';
    detectedLights.append(item);
  } else {
    metrics.detected_light_sources.forEach((source) => {
      const item = document.createElement('li');
      item.textContent = `${source.role.replaceAll('_', ' ')} · ${source.direction.replaceAll('_', ' ')} · intensity ${Math.round(source.intensity * 100)}% · ${source.softness} · confidence ${Math.round(source.confidence * 100)}%`;
      detectedLights.append(item);
    });
  }
  const screenResult = frameResult.analyzers.find((analyzer) => analyzer.module_id === 'screen-quality');
  const screenMetrics = screenResult?.metrics as ScreenQualityMetrics | undefined;
  screenLightType.textContent = screenMetrics?.screen_type.toUpperCase() ?? 'UNKNOWN';
  screenLightUniformity.textContent = `${Math.round(metrics.background_uniformity * 100)}%`;
  screenLightCount.textContent = String(metrics.background_light_sources.filter((source) => source.role === 'background_screen_light').length);
  lightingWarnings.replaceChildren();
  if (!result.warnings.length) {
    const item = document.createElement('li');
    item.className = 'clear';
    item.textContent = 'No lighting warnings.';
    lightingWarnings.append(item);
    return;
  }
  result.warnings.forEach((warning) => {
    const item = document.createElement('li');
    item.textContent = warning.message;
    lightingWarnings.append(item);
  });
}

function updateExposureSummary(frameResult: LimonadeEyeFrameResult): void {
  const result = frameResult.analyzers.find((analyzer) => analyzer.module_id === 'exposure');
  if (!result) return;
  const metrics = result.metrics as ExposureMetrics;
  exposureStatus.textContent = metrics.exposure_status.replaceAll('_', ' ').toUpperCase();
  averageLuma.textContent = metrics.average_luma.toFixed(1);
  contrastScore.textContent = `${Math.round(metrics.contrast_score * 100)}%`;
  highlightClip.textContent = `${metrics.highlight_clip_percentage.toFixed(2)}%`;
  shadowClip.textContent = `${metrics.shadow_clip_percentage.toFixed(2)}%`;
  renderLuminanceHistogram(lumaHistogram, histogramContext, metrics.luminance_histogram);
  exposureWarnings.replaceChildren();
  if (!result.warnings.length) {
    const item = document.createElement('li');
    item.className = 'clear';
    item.textContent = 'No exposure warnings.';
    exposureWarnings.append(item);
    return;
  }
  result.warnings.forEach((warning) => {
    const item = document.createElement('li');
    item.textContent = warning.message;
    exposureWarnings.append(item);
  });
}

function updateEdgeSummary(frameResult: LimonadeEyeFrameResult): void {
  const result = frameResult.analyzers.find((analyzer) => analyzer.module_id === 'edge-confidence');
  if (!result) return;
  const metrics = result.metrics as EdgeConfidenceMetrics;
  edgeScore.textContent = `${Math.round(metrics.edge_confidence_score * 100)}%`;
  weakEdges.textContent = `${metrics.weak_edge_percentage.toFixed(1)}%`;
  chromaSeparation.textContent = `${Math.round(metrics.chroma_separation_score * 100)}%`;
  edgeWarnings.replaceChildren();
  if (!result.warnings.length) {
    const item = document.createElement('li');
    item.className = 'clear';
    item.textContent = 'No edge confidence warnings.';
    edgeWarnings.append(item);
    return;
  }
  result.warnings.forEach((warning) => {
    const item = document.createElement('li');
    item.textContent = warning.message;
    edgeWarnings.append(item);
  });
}

function updateArtifactsSummary(frameResult: LimonadeEyeFrameResult): void {
  const result = frameResult.analyzers.find((analyzer) => analyzer.module_id === 'video-artifacts') as VideoArtifactsResult | undefined;
  if (!result) return;
  artifactScore.textContent = `${Math.round(result.artifact_score * 100)}%`;
  artifactRisk.textContent = result.overall_artifact_risk.toUpperCase();
  detectedArtifacts.replaceChildren();
  if (!result.detected_artifacts.length) {
    const item = document.createElement('li');
    item.className = 'clear';
    item.textContent = 'No significant artifacts detected.';
    detectedArtifacts.append(item);
  } else {
    result.detected_artifacts.forEach((artifact) => {
      const item = document.createElement('li');
      item.textContent = `${artifact.type.replaceAll('_', ' ')} · ${artifact.severity} · confidence ${Math.round(artifact.confidence * 100)}%`;
      detectedArtifacts.append(item);
    });
  }
  artifactWarnings.replaceChildren();
  if (!result.warnings.length) {
    const item = document.createElement('li');
    item.className = 'clear';
    item.textContent = 'No artifact warnings.';
    artifactWarnings.append(item);
  } else {
    result.warnings.forEach((warning) => {
      const item = document.createElement('li');
      item.textContent = warning.message;
      artifactWarnings.append(item);
    });
  }
}

function updateMarkerSummary(frameResult: LimonadeEyeFrameResult): void {
  const result = frameResult.analyzers.find((analyzer) => analyzer.module_id === 'tracking-markers');
  if (!result) return;
  const metrics = result.metrics as TrackingMarkerMetrics;
  markerCount.textContent = String(metrics.marker_count);
  markerDistribution.textContent = metrics.distribution_status.toUpperCase();
  markerDistributionScore.textContent = `${Math.round(metrics.distribution_score * 100)}%`;
  markerCandidates.textContent = String(metrics.marker_candidate_count_before_filtering);
  markerFilteredCount.textContent = String(metrics.marker_count_after_filtering);
  const engineTiming = frameResult.analyzer_timings_ms['tracking-markers'];
  markerProcessingTime.textContent = `${(engineTiming ?? metrics.processing_time_ms).toFixed(2)} ms`;
  rawMarkerOutput.textContent = JSON.stringify(result, null, 2);
  detectedMarkers.replaceChildren();
  if (!metrics.markers.length) {
    const item = document.createElement('li');
    item.textContent = 'No reliable markers detected.';
    detectedMarkers.append(item);
  } else {
    metrics.markers.forEach((marker) => {
      const item = document.createElement('li');
      item.textContent = `${marker.id} · ${marker.type} · (${marker.x.toFixed(1)}, ${marker.y.toFixed(1)}) · confidence ${Math.round(marker.confidence * 100)}%`;
      detectedMarkers.append(item);
    });
  }
  markerWarnings.replaceChildren();
  if (!result.warnings.length) {
    const item = document.createElement('li');
    item.className = 'clear';
    item.textContent = 'Marker distribution looks usable.';
    markerWarnings.append(item);
  } else {
    result.warnings.forEach((warning) => {
      const item = document.createElement('li');
      item.textContent = warning.message;
      markerWarnings.append(item);
    });
  }
}

function updateMotionSummary(frameResult: LimonadeEyeFrameResult): void {
  const result = frameResult.analyzers.find((analyzer) => analyzer.module_id === 'optical-flow');
  if (!result) return;
  const metrics = result.metrics as OpticalFlowMetrics;
  cameraMovement.textContent = metrics.possible_camera_movement.replaceAll('_', ' ').toUpperCase();
  motionMagnitude.textContent = `${metrics.motion_magnitude.toFixed(2)} px`;
  motionDirection.textContent = metrics.motion_direction.toUpperCase();
  shakeScore.textContent = `${Math.round(metrics.shake_score * 100)}%`;
  motionStability.textContent = `${Math.round(metrics.motion_stability * 100)}%`;
  motionProcessingTime.textContent = `${metrics.processing_time_ms.toFixed(2)} ms`;
  motionWarnings.replaceChildren();
  if (!result.warnings.length) {
    const item = document.createElement('li');
    item.className = 'clear';
    item.textContent = 'Motion estimate is stable.';
    motionWarnings.append(item);
  } else {
    result.warnings.forEach((warning) => {
      const item = document.createElement('li');
      item.textContent = warning.message;
      motionWarnings.append(item);
    });
  }
}

function renderCurrentOverlays(): void {
  if (!lastFrameResult) return;
  const enabledByModule: Record<string, boolean> = {
    'screen-quality': screenOverlayToggle.checked,
    'spill-detection': spillOverlayToggle.checked,
    'lighting-setup': lightingOverlayToggle.checked,
    exposure: exposureOverlayToggle.checked,
    'edge-confidence': edgeOverlayToggle.checked,
    'video-artifacts': artifactOverlayToggle.checked,
    'tracking-markers': markerOverlayToggle.checked,
    'optical-flow': motionOverlayToggle.checked,
  };
  const layers = lastFrameResult.analyzers.map((analyzer) => ({
    moduleId: analyzer.module_id,
    moduleName: analyzer.module_name,
    enabled: enabledByModule[analyzer.module_id] ?? true,
    overlays: analyzer.overlays,
  }));
  const stats = renderOverlayLayers(
    overlayCanvas,
    overlayContext,
    lastFrameResult.width,
    lastFrameResult.height,
    layers,
  );
  overlayRenderStatus.textContent = `${stats.renderedPrimitiveCount} primitive${stats.renderedPrimitiveCount === 1 ? '' : 's'}`;
  overlayLegend.replaceChildren();
  const visibleLayers = layers.filter((layer) => layer.enabled && layer.overlays.length);
  if (!visibleLayers.length) {
    const empty = document.createElement('span');
    empty.textContent = 'No selected analyzer returned overlays.';
    overlayLegend.append(empty);
    return;
  }
  visibleLayers.forEach((layer) => {
    const item = document.createElement('span');
    item.className = 'overlay-legend-item';
    const swatch = document.createElement('i');
    swatch.style.background = layer.overlays[0]?.color ?? '#dfff57';
    const label = document.createElement('b');
    label.textContent = `${layer.moduleName} · ${layer.overlays.length}`;
    item.append(swatch, label);
    overlayLegend.append(item);
  });
}

async function captureAndAnalyze(): Promise<void> {
  if (analysisInFlight || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return;
  analysisInFlight = true;
  status.textContent = 'Analyzing locally…';

  try {
    const width = video.videoWidth;
    const height = video.videoHeight;
    const frame = captureVideoFrame(video, canvas, context);
    const frameResult = await engine.analyzeFrame(frame, {
      timestampMs: video.currentTime * 1000,
      performanceMode: currentPerformanceMode(),
    });
    lastFrameResult = frameResult;
    frames.push(frameResult);
    updateQualitySummary(frameResult);
    updateSpillSummary(frameResult);
    updateLightingSummary(frameResult);
    updateExposureSummary(frameResult);
    updateEdgeSummary(frameResult);
    updateDebugInfo(frameResult);
    updateArtifactsSummary(frameResult);
    updateMarkerSummary(frameResult);
    updateMotionSummary(frameResult);
    renderCurrentOverlays();
    output.textContent = JSON.stringify(currentReport(), null, 2);
    updateReportPreview();
    status.textContent = `Frame ${frameResult.frame_number} · ${width}×${height} · ${frameResult.processing_duration_ms} ms · ${currentPerformanceMode()}`;
  } catch (error) {
    status.textContent = 'Analysis failed';
    output.textContent = JSON.stringify({ error: error instanceof Error ? error.message : String(error) }, null, 2);
  } finally {
    analysisInFlight = false;
  }
}

function stopPlaybackCapture(): void {
  if (playbackTimer !== undefined) window.clearInterval(playbackTimer);
  playbackTimer = undefined;
}

videoInput.addEventListener('change', () => {
  const file = videoInput.files?.[0];
  if (!file) return;
  selectedFile = file;
  frames.length = 0;
  engine.reset();
  reportSessionId = globalThis.crypto?.randomUUID?.() ?? `eye-${Date.now()}`;
  reportCreatedAt = new Date().toISOString();
  screenType.textContent = '—';
  qualityScore.textContent = '—';
  coverage.textContent = '—';
  uniformity.textContent = '—';
  frameNumber.textContent = '—';
  frameTimestamp.textContent = '—';
  analysisTime.textContent = '—';
  analyzerTimings.replaceChildren();
  const waitingTiming = document.createElement('li');
  waitingTiming.textContent = 'Waiting for analysis.';
  analyzerTimings.append(waitingTiming);
  rawOutput.textContent = '{ "status": "waiting_for_frame" }';
  spillRisk.textContent = '—';
  spillScore.textContent = '—';
  spillArea.textContent = '—';
  spillWarnings.replaceChildren();
  const waitingSpillWarning = document.createElement('li');
  waitingSpillWarning.textContent = 'Waiting for the first frame.';
  spillWarnings.append(waitingSpillWarning);
  lightingPattern.textContent = '—';
  dominantLight.textContent = '—';
  lightingBalance.textContent = '—';
  lightingRatio.textContent = '—';
  detectedLights.replaceChildren();
  const waitingLightSource = document.createElement('li');
  waitingLightSource.textContent = 'Waiting for the first frame.';
  detectedLights.append(waitingLightSource);
  screenLightType.textContent = '—';
  screenLightUniformity.textContent = '—';
  screenLightCount.textContent = '—';
  lightingWarnings.replaceChildren();
  const waitingLightingWarning = document.createElement('li');
  waitingLightingWarning.textContent = 'Waiting for the first frame.';
  lightingWarnings.append(waitingLightingWarning);
  exposureStatus.textContent = '—';
  averageLuma.textContent = '—';
  contrastScore.textContent = '—';
  highlightClip.textContent = '—';
  shadowClip.textContent = '—';
  renderLuminanceHistogram(lumaHistogram, histogramContext, []);
  exposureWarnings.replaceChildren();
  const waitingExposureWarning = document.createElement('li');
  waitingExposureWarning.textContent = 'Waiting for the first frame.';
  exposureWarnings.append(waitingExposureWarning);
  edgeScore.textContent = '—';
  weakEdges.textContent = '—';
  chromaSeparation.textContent = '—';
  edgeWarnings.replaceChildren();
  const waitingEdgeWarning = document.createElement('li');
  waitingEdgeWarning.textContent = 'Waiting for the first frame.';
  edgeWarnings.append(waitingEdgeWarning);
  artifactScore.textContent = '—';
  artifactRisk.textContent = '—';
  detectedArtifacts.replaceChildren();
  artifactWarnings.replaceChildren();
  const waitingArtifact = document.createElement('li');
  waitingArtifact.textContent = 'Waiting for the first frame.';
  detectedArtifacts.append(waitingArtifact);
  const waitingArtifactWarning = document.createElement('li');
  waitingArtifactWarning.textContent = 'Waiting for the first frame.';
  artifactWarnings.append(waitingArtifactWarning);
  markerCount.textContent = '—';
  markerDistribution.textContent = '—';
  markerDistributionScore.textContent = '—';
  markerCandidates.textContent = '—';
  markerFilteredCount.textContent = '—';
  markerProcessingTime.textContent = '—';
  rawMarkerOutput.textContent = '{ "status": "waiting_for_frame" }';
  detectedMarkers.replaceChildren();
  markerWarnings.replaceChildren();
  const waitingMarker = document.createElement('li');
  waitingMarker.textContent = 'Waiting for the first frame.';
  detectedMarkers.append(waitingMarker);
  const waitingMarkerWarning = document.createElement('li');
  waitingMarkerWarning.textContent = 'Waiting for the first frame.';
  markerWarnings.append(waitingMarkerWarning);
  cameraMovement.textContent = '—';
  motionMagnitude.textContent = '—';
  motionDirection.textContent = '—';
  shakeScore.textContent = '—';
  motionStability.textContent = '—';
  motionProcessingTime.textContent = '—';
  motionWarnings.replaceChildren();
  const waitingMotionWarning = document.createElement('li');
  waitingMotionWarning.textContent = 'Waiting for consecutive frames.';
  motionWarnings.append(waitingMotionWarning);
  lastFrameResult = undefined;
  overlayContext.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);
  qualityWarnings.replaceChildren();
  const waitingWarning = document.createElement('li');
  waitingWarning.textContent = 'Waiting for the first frame.';
  qualityWarnings.append(waitingWarning);
  stopPlaybackCapture();
  if (videoUrl) URL.revokeObjectURL(videoUrl);
  videoUrl = URL.createObjectURL(file);
  video.src = videoUrl;
  emptyState.hidden = true;
  status.textContent = `Loading ${file.name}…`;
  output.textContent = JSON.stringify(currentReport(), null, 2);
  updateReportPreview();
});

video.addEventListener('loadeddata', () => {
  document.querySelector<HTMLElement>('.stage')?.classList.add('loaded');
  analyzeButton.disabled = false;
  status.textContent = 'Ready to analyze';
  void captureAndAnalyze();
  if (selfTestMode) {
    video.muted = true;
    void video.play();
  }
});
video.addEventListener('play', () => {
  stopPlaybackCapture();
  playbackTimer = window.setInterval(() => void captureAndAnalyze(), playbackCadenceMs[currentPerformanceMode()]);
});
video.addEventListener('pause', stopPlaybackCapture);
video.addEventListener('ended', stopPlaybackCapture);
video.addEventListener('error', () => {
  status.textContent = 'This video could not be loaded';
  analyzeButton.disabled = true;
});
analyzeButton.addEventListener('click', () => void captureAndAnalyze());
performanceModeSelect.addEventListener('change', () => {
  if (!video.paused && !video.ended) {
    stopPlaybackCapture();
    playbackTimer = window.setInterval(() => void captureAndAnalyze(), playbackCadenceMs[currentPerformanceMode()]);
  }
  updateAnalyzerStatus();
});
document.querySelectorAll<HTMLInputElement>('input[name="report-scope"]').forEach((input) => {
  input.addEventListener('change', updateReportPreview);
});
exportReportButton.addEventListener('click', () => {
  const reportFrames = selectedReportFrames();
  if (!reportFrames.length) return;
  const json = jsonExporter.export({ frames: reportFrames, options: reportOptions() });
  reportPreview.textContent = json;
  updateReportPreview();
  downloadReportLink.click();
});
motionLinkFileInput.addEventListener('change', () => {
  const file = motionLinkFileInput.files?.[0];
  if (!file) return;
  void file.text().then((text) => {
    lastMotionLinkFileText = text;
    motionLinkJsonPaste.value = text;
    return processMotionLinkText(text, file.name);
  });
});
motionLinkLoadPaste.addEventListener('click', () => {
  const text = motionLinkJsonPaste.value.trim();
  if (!text) {
    motionLinkProcessorStatus.textContent = 'Paste MotionLink JSON first.';
    return;
  }
  lastMotionLinkFileText = text;
  void processMotionLinkText(text, 'pasted MotionLink JSON');
});
motionLinkLoadSample.addEventListener('click', () => {
  const text = JSON.stringify(sampleMotionLink, null, 2);
  motionLinkJsonPaste.value = text;
  lastMotionLinkFileText = text;
  void processMotionLinkText(text, 'docs/sample-motionlink.json');
});
motionLinkUseOpticalFlow.addEventListener('change', () => {
  if (!lastMotionLinkFileText) return;
  void processMotionLinkText(lastMotionLinkFileText, motionLinkFileName.textContent || 'MotionLink JSON');
});
motionLinkPlayToggle.addEventListener('click', () => {
  if (motionLinkIsPlaying) {
    motionLinkVisualizer.pause();
    motionLinkIsPlaying = false;
    motionLinkPlayToggle.textContent = 'Play';
  } else {
    motionLinkVisualizer.play();
    motionLinkIsPlaying = true;
    motionLinkPlayToggle.textContent = 'Pause';
  }
});
motionLinkRestart.addEventListener('click', () => {
  motionLinkIsPlaying = true;
  motionLinkPlayToggle.textContent = 'Pause';
  motionLinkVisualizer.restart();
});
motionLinkPlaybackSpeed.addEventListener('change', () => {
  motionLinkVisualizer.setPlaybackSpeed(Number(motionLinkPlaybackSpeed.value));
});
motionLinkTimeline.addEventListener('input', () => {
  motionLinkVisualizer.setTime(Number(motionLinkTimeline.value));
});
motionLinkPathMode.addEventListener('change', updateMotionLinkVisualizerOptions);
motionLinkAnimateSourceToggle.addEventListener('click', () => {
  motionLinkAnimateSource = motionLinkAnimateSource === 'raw' ? 'cleaned' : 'raw';
  updateMotionLinkVisualizerOptions();
});
[
  motionLinkShowGrid,
  motionLinkShowPath,
  motionLinkShowKeyframes,
  motionLinkShowConfidence,
].forEach((input) => input.addEventListener('change', updateMotionLinkVisualizerOptions));
[motionLinkCleanEnabled, motionLinkCleanStrength].forEach((input) => {
  input.addEventListener('change', () => {
    if (lastMotionLinkFileText) {
      void processMotionLinkText(lastMotionLinkFileText, motionLinkFileName.textContent || 'MotionLink JSON');
    } else if (lastMotionLinkLoadResult) {
      renderMotionLinkVisualization(lastMotionLinkLoadResult);
    }
  });
});
motionLinkExportSnapshot.addEventListener('click', () => {
  const link = document.createElement('a');
  link.href = motionLinkVisualizerCanvas.toDataURL('image/png');
  link.download = `motionlink-visualizer-${Date.now()}.png`;
  link.click();
});
motionLinkDownloadProcessed.addEventListener('click', () => {
  if (!motionLinkDownloadUrl || !lastMotionLinkResult) return;
  const link = document.createElement('a');
  link.href = motionLinkDownloadUrl;
  link.download = `motionlink-processed-${lastMotionLinkResult.session_id}.json`;
  link.click();
});
motionLinkDownloadUsd.addEventListener('click', () => {
  if (!lastMotionLinkResult) return;
  downloadTextFile(
    `motionlink-camera-${lastMotionLinkResult.session_id}.usda`,
    exportMotionLinkUSD(lastMotionLinkResult),
    'text/plain',
  );
});
motionLinkDownloadFbx.addEventListener('click', () => {
  if (!lastMotionLinkResult) return;
  downloadTextFile(
    `motionlink-camera-${lastMotionLinkResult.session_id}.fbx`,
    exportMotionLinkFBX(lastMotionLinkResult),
    'text/plain',
  );
});
screenOverlayToggle.addEventListener('change', renderCurrentOverlays);
spillOverlayToggle.addEventListener('change', renderCurrentOverlays);
lightingOverlayToggle.addEventListener('change', renderCurrentOverlays);
exposureOverlayToggle.addEventListener('change', renderCurrentOverlays);
edgeOverlayToggle.addEventListener('change', renderCurrentOverlays);
artifactOverlayToggle.addEventListener('change', renderCurrentOverlays);
markerOverlayToggle.addEventListener('change', renderCurrentOverlays);
motionOverlayToggle.addEventListener('change', renderCurrentOverlays);
motionEnabledToggle.addEventListener('change', () => {
  if (motionEnabledToggle.checked) {
    motionAnalyzer.reset();
    engine.registerAnalyzer(motionAnalyzer);
    motionOverlayToggle.disabled = false;
    cameraMovement.textContent = 'WAITING FOR HISTORY';
    motionWarnings.replaceChildren();
    const item = document.createElement('li');
    item.textContent = 'Waiting for consecutive frames.';
    motionWarnings.append(item);
  } else {
    engine.unregisterAnalyzer(motionAnalyzer.id);
    motionAnalyzer.reset();
    motionOverlayToggle.disabled = true;
    cameraMovement.textContent = 'DISABLED';
    motionMagnitude.textContent = '—';
    motionDirection.textContent = '—';
    shakeScore.textContent = '—';
    motionStability.textContent = '—';
    motionProcessingTime.textContent = '—';
    motionWarnings.replaceChildren();
    const item = document.createElement('li');
    item.textContent = 'Optical Flow analyzer is disabled.';
    motionWarnings.append(item);
  }
  updateAnalyzerStatus();
  renderCurrentOverlays();
});
window.addEventListener('beforeunload', () => {
  stopPlaybackCapture();
  motionLinkVisualizer.destroy();
  if (videoUrl) URL.revokeObjectURL(videoUrl);
  if (reportDownloadUrl) URL.revokeObjectURL(reportDownloadUrl);
  if (motionLinkDownloadUrl) URL.revokeObjectURL(motionLinkDownloadUrl);
});

if (selfTestMode) {
  status.textContent = 'Generating local self-test video…';
  void installChromaSelfTest(videoInput).catch((error: unknown) => {
    status.textContent = 'Self-test video generation failed';
    rawOutput.textContent = JSON.stringify({
      error: error instanceof Error ? error.message : String(error),
    }, null, 2);
  });
}
